import { Component, computed, input } from '@angular/core'
import { ColorName } from '../../design-system'
import { Icon } from '../../icon'
import { Illustration } from '../../illustration'
import { PlaceholderStateComponent, PlaceholderStateSize } from '../placeholder-state'

/**
 * Themed wrapper component over `ftr-placeholder-state`. Displays an empty state for when
 * content has loaded but there is nothing to show. Optional `ng-content` is projected underneath
 * the message.
 *
 * TODO VS-206 - Replace EmptyStateComponent, then rename this
 *
 * @example
 * ```html
 * <ftr-empty-state
 *   title="No Recording Requests"
 *   message="When customers place requests for recordings, they will appear here."
 *   theme="RecordingRequest"
 * >
 *   <button>Click me!</button>
 * </ftr-empty-state>
 * ```
 */
@Component({
  selector: 'ftr-empty-state-new',
  templateUrl: './empty-state.component.html',
  imports: [PlaceholderStateComponent],
})
export class EmptyStateNewComponent {
  readonly title = input<string>()
  readonly message = input<string>()
  readonly size = input<PlaceholderStateSize>(PlaceholderStateSize.FullPage)
  readonly theme = input<EmptyStateThemeName | EmptyStateConfig>('Search')

  protected readonly resolvedConfig = computed<EmptyStateConfig>(() => {
    const theme = this.theme()
    return typeof theme === 'string' ? EmptyStateTheme[theme] : theme
  })
}

export interface EmptyStateConfig {
  icon: Icon
  iconColor?: ColorName
  illustration?: Illustration
}

// TODO - Add missing line icons and illustrations, then add remaining themes
export const EmptyStateTheme = {
  Cart: { icon: 'Cart', iconColor: 'Grey40', illustration: 'cart' },
  CourtSystem: { icon: 'Gavel', iconColor: 'Grey40', illustration: 'court-system' },
  Courthouse: { icon: 'CourthouseLine', iconColor: 'Grey40', illustration: 'courthouse' },
  Courtroom: { icon: 'CourtroomLine', iconColor: 'Grey40', illustration: 'courtroom' },
  Department: { icon: 'Department', iconColor: 'Grey40', illustration: 'department' },
  Requests: { icon: 'Requests', iconColor: 'Watermelon40', illustration: 'requests' },
  RecordingRequest: { icon: 'RecordingRequest', iconColor: 'Primary40', illustration: 'recording-requests' },
  Search: { icon: 'Search', iconColor: 'Primary40', illustration: 'search' },
} as const satisfies Record<string, EmptyStateConfig>

export type EmptyStateThemeName = keyof typeof EmptyStateTheme
